import i18n from "i18next";
import { faEllipsisVertical, faPen } from "@fortawesome/pro-regular-svg-icons";
import { getCustomerOrderOfferStateLabel } from "src/api/generated/erp/db/types/enums/customerOrderOfferState.ts";
import { CustomerOrderOfferView } from "src/api/generated/erp/db/types/tables/customerOrderOfferView.ts";
import { CustomerOfferSummary } from "src/api/generated/erp/sales/customerOrderOffer/service/customerOfferSummary.ts";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { openFormOnDialog } from "src/components/common/dialogs/formDialog/openFormOnDialog.ts";
import { LabelValueViewItem } from "src/components/common/misc/LabelValueView.tsx";
import { ObjectInfoView } from "src/components/common/misc/ObjectInfoView.tsx";
import { formatMoneyWithCurrency } from "src/utils/numberUtils.ts";
import { AavoMenuButton } from "src/components/common/menu/AavoMenuButton.tsx";
import { CustomerOrderOfferDataGridRowContextMenu } from "src/components/views/erp/sales/customerOrderOffer/CustomerOrderOfferDataGridRowContextMenu.tsx";
import { CustomerOrderOfferForm } from "src/components/views/erp/sales/customerOrderOffer/CustomerOrderOfferForm.tsx";
import {
	CustomerOrderOrOfferSummaryField,
	useCustomerOrderOrOfferSummaryFields,
} from "src/components/views/erp/sales/customerOrderLine/utils/customerOrderOrOfferSummaryFields.ts";
import { formatIsoString } from "src/utils/dayjsUtils.ts";
import { nullIfBlank } from "src/utils/strings.tsx";
import { RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { getCountryLabel } from "src/components/common/country/countries.ts";
import { OpenSalesLeadButton } from "src/components/views/erp/utilComponents/OpenSalesLeadButton.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { ObjectAttributeValueWithField } from "src/api/generated/erp/common/objectAttributes/model/objectAttributeValueWithField.ts";
import { getObjectAttributeLabelValueSection } from "src/components/views/erp/objectAttributes/objectAttributeLabelValueSection.ts";

export interface CustomerOfferInfoViewProps {
	customerOrderOffer: CustomerOrderOfferView;
	summary: CustomerOfferSummary;
	attributes: ObjectAttributeValueWithField[];
	refreshData: () => Promise<void>;
	offerLinesRefreshRef: RefreshableElementRef;
	editable?: boolean;
}

export const CustomerOfferInfoView = ({
	customerOrderOffer,
	summary,
	attributes,
	refreshData,
	offerLinesRefreshRef,
	editable = true,
}: CustomerOfferInfoViewProps) => {
	const { openDialog } = useGenericDialog();
	const priceAndCostFields = useCustomerOrderOrOfferSummaryFields("CUSTOMER_OFFER", summary);
	const hasPermission = useHasPermission();
	const { tenantConfig } = useTenantCustomizations();
	const showSalesLead = tenantConfig.erp.crmEnabled && hasPermission(Permissions.Sales.SalesLeads.READ);

	return (
		<ObjectInfoView
			title={customerOrderOffer.customerName}
			subtitleItems={[
				`${i18n.t("offer")} ${customerOrderOffer.customerOrderOfferId}`,
				getCustomerOrderOfferStateLabel(customerOrderOffer.customerOrderOfferState),
				customerOrderOffer.customerOfferTypeName,
			]}
			keyFacts={[
				summary.customerType === "COMPANY" ?
					{
						label: i18n.t("sum"),
						value: formatMoneyWithCurrency(summary.totalPrice, summary.currencyCode),
					}
				:	{
						label: i18n.t("sum_with_vat"),
						value: formatMoneyWithCurrency(summary.totalPriceWithVat, summary.currencyCode),
					},
				{ label: i18n.t("valid_until"), value: getOfferValidityLabel(customerOrderOffer) },
				{ label: i18n.t("salesperson"), value: customerOrderOffer.salespersonName },
			]}
			actions={
				editable && (
					<>
						<AsyncButton
							icon={faPen}
							tooltip={i18n.t("edit")}
							size={"small"}
							onClick={() => {
								openFormOnDialog({
									openDialog,
									component: CustomerOrderOfferForm,
									props: {
										customerOrderOfferId: customerOrderOffer.customerOrderOfferId,
									},
									title: i18n.t("offer"),
									agentContext: {
										key: defineAgentUiContextKey("erp.sales.customerOffer.formDialog"),
										data: {
											customerOrderOfferId: customerOrderOffer.customerOrderOfferId,
											customerOrderId: customerOrderOffer.customerOrderId ?? null,
											customerId: customerOrderOffer.customerId,
										},
									},
									size: "lg",
									onSubmit: async () => {
										await refreshData();
									},
								});
							}}
						/>
						<AavoMenuButton
							icon={faEllipsisVertical}
							tooltip={i18n.t("more")}
							size={"small"}
							menuContent={
								<CustomerOrderOfferDataGridRowContextMenu
									row={customerOrderOffer}
									allSelectedRows={[customerOrderOffer]}
									refreshData={refreshData}
									offerLinesRefreshRef={offerLinesRefreshRef}
								/>
							}
						/>
					</>
				)
			}
			items={getCustomerOrderOfferInfoItems({
				customerOrderOffer,
				priceAndCostFields,
				attributes,
				showSalesLead,
			})}
		/>
	);
};

function getOfferValidityLabel(customerOrderOffer: CustomerOrderOfferView): string | null {
	if (customerOrderOffer.validUntil != null) {
		return formatIsoString(customerOrderOffer.validUntil);
	}
	if (customerOrderOffer.validForDays != null) {
		return i18n.t("valid_for_days_param_days", {
			days: customerOrderOffer.validForDays,
		});
	}
	return null;
}

interface CustomerOrderOfferInfoItemsParams {
	customerOrderOffer: CustomerOrderOfferView;
	priceAndCostFields: CustomerOrderOrOfferSummaryField[];
	attributes: ObjectAttributeValueWithField[];
	showSalesLead: boolean;
}

function getCustomerOrderOfferInfoItems({
	customerOrderOffer,
	priceAndCostFields,
	attributes,
	showSalesLead,
}: CustomerOrderOfferInfoItemsParams): LabelValueViewItem[] {
	return [
		{
			type: "section",
			label: i18n.t("general"),
			fields: [
				{
					label: i18n.t("business_id"),
					value: customerOrderOffer.customerBusinessId,
				},
				{
					label: i18n.t("customer_order"),
					value: customerOrderOffer.customerOrderId,
				},
				{
					label: i18n.t("sales_lead"),
					value:
						showSalesLead && customerOrderOffer.salesLeadId != null ?
							<OpenSalesLeadButton
								salesLeadId={customerOrderOffer.salesLeadId}
								label={customerOrderOffer.salesLeadId.toString()}
								variant={"text"}
								size={"small"}
								sx={{ justifySelf: "start" }}
							/>
						:	null,
				},
				{
					label: i18n.t("site"),
					value: customerOrderOffer.siteName,
				},
				{
					label: i18n.t("reference"),
					value: customerOrderOffer.orderReference,
				},
				{
					label: i18n.t("customer_offer_request_no"),
					value: customerOrderOffer.customerOfferRequestNo,
				},
				{
					label: i18n.t("external_order_id"),
					value: customerOrderOffer.externalOrderId,
				},
				{
					label: i18n.t("customer_group"),
					value: customerOrderOffer.customerGroupName,
				},
				{
					label: i18n.t("price_group"),
					value: customerOrderOffer.priceGroupName,
				},
				{
					label: i18n.t("currency"),
					value: customerOrderOffer.currencyCode,
				},
				{
					label: i18n.t("domestic_currency"),
					value: customerOrderOffer.domesticCurrencyCode,
				},
				{
					label: i18n.t("frozen"),
					value: customerOrderOffer.frozen,
				},
				{
					label: i18n.t("planned_delivery_date"),
					value: formatIsoString(customerOrderOffer.plannedDeliveryDate),
				},
				{
					label: i18n.t("contract_date"),
					value: formatIsoString(customerOrderOffer.contractDate),
				},
				{
					label: i18n.t("created_by"),
					value: customerOrderOffer.createdBy,
				},
				{
					label: i18n.t("created_at"),
					value: formatIsoString(customerOrderOffer.createdAt),
				},
				{
					label: i18n.t("offered_at"),
					value: formatIsoString(customerOrderOffer.offeredAt),
				},
				{
					label: i18n.t("printed_at"),
					value: formatIsoString(customerOrderOffer.printedAt),
				},
				{
					label: i18n.t("closed_at"),
					value: formatIsoString(customerOrderOffer.closedAt),
				},
				{
					label: i18n.t("note"),
					value: nullIfBlank(customerOrderOffer.note),
					multiline: true,
				},
			],
		},
		{
			type: "section",
			label: i18n.t("responsible_person"),
			fields: [
				{
					label: i18n.t("name"),
					value: customerOrderOffer.responsiblePersonName,
				},
				{
					label: i18n.t("phone"),
					value: customerOrderOffer.responsiblePersonPhone,
				},
				{
					label: i18n.t("email"),
					value: customerOrderOffer.responsiblePersonEmail,
				},
			],
		},
		{
			type: "section",
			label: i18n.t("salesperson"),
			fields: [
				{
					label: i18n.t("phone"),
					value: customerOrderOffer.salespersonPhone,
				},
				{
					label: i18n.t("email"),
					value: customerOrderOffer.salespersonEmail,
				},
			],
		},
		{
			type: "section",
			label: i18n.t("price_and_cost"),
			fields: priceAndCostFields,
		},
		{
			type: "section",
			label: i18n.t("contact_person"),
			fields: [
				{
					label: i18n.t("name"),
					value: customerOrderOffer.contactName,
				},
				{
					label: i18n.t("phone"),
					value: customerOrderOffer.contactPhone,
				},
				{
					label: i18n.t("email"),
					value: customerOrderOffer.contactEmail,
				},
			],
		},
		{
			type: "section",
			label: i18n.t("delivery"),
			fields: [
				{
					label: i18n.t("delivery_method"),
					value: customerOrderOffer.deliveryMethodCode,
				},
				{
					label: i18n.t("delivery_terms"),
					value: customerOrderOffer.deliveryTermsCode,
				},
				{
					label: i18n.t("delivery_terms_destination"),
					value: customerOrderOffer.deliveryTermsDestination,
				},
				{
					label: i18n.t("delivery_duration"),
					value: customerOrderOffer.deliveryDurationDays,
				},
				{
					label: i18n.t("payment_term"),
					value: customerOrderOffer.paymentTermDesc,
				},
				{
					label: i18n.t("contractual_term"),
					value: customerOrderOffer.contractualTermCode,
				},
				{
					label: i18n.t("vat_handling_type"),
					value: customerOrderOffer.vatHandlingTypeName,
				},
			],
		},
		{
			type: "section",
			label: i18n.t("delivery_address"),
			fields: [
				{
					label: i18n.t("description"),
					value: customerOrderOffer.deliveryName,
				},
				{
					label: i18n.t("recipient"),
					value: customerOrderOffer.deliveryRecipientName,
				},
				{
					label: i18n.t("address_1"),
					value: customerOrderOffer.deliveryAddress_1,
				},
				{
					label: i18n.t("address_2"),
					value: customerOrderOffer.deliveryAddress_2,
				},
				{
					label: i18n.t("postal_code"),
					value: customerOrderOffer.deliveryPostalCode,
				},
				{
					label: i18n.t("city"),
					value: customerOrderOffer.deliveryCity,
				},
				{
					label: i18n.t("country"),
					value: getCountryLabel(customerOrderOffer.deliveryCountry),
				},
				{
					label: i18n.t("contact"),
					value: customerOrderOffer.deliveryContact,
				},
			],
		},
		{
			type: "section",
			label: i18n.t("invoice_address"),
			fields: [
				{
					label: i18n.t("description"),
					value: customerOrderOffer.invoiceAddressName,
				},
				{
					label: i18n.t("address_1"),
					value: customerOrderOffer.invoiceAddressAddress_1,
				},
				{
					label: i18n.t("address_2"),
					value: customerOrderOffer.invoiceAddressAddress_2,
				},
				{
					label: i18n.t("postal_code"),
					value: customerOrderOffer.invoiceAddressPostalCode,
				},
				{
					label: i18n.t("city"),
					value: customerOrderOffer.invoiceAddressCity,
				},
				{
					label: i18n.t("country"),
					value: getCountryLabel(customerOrderOffer.invoiceAddressCountry),
				},
				{
					label: i18n.t("phone"),
					value: customerOrderOffer.invoiceAddressPhone,
				},
				{
					label: i18n.t("email"),
					value: customerOrderOffer.invoiceAddressEmail,
				},
			],
		},
		getObjectAttributeLabelValueSection(attributes),
	];
}
